// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { EyeCheckIcon, EyeErrorIcon, EyeIcon } from "src/assets/icons";

// Types
import { ExamType } from "src/services/exams/types";

// Constants
import { examCardEnums, eyeTypeEnums } from "src/constants/enums";

// Utils
import {
  isBinocular,
  isMonocularMultipleSections,
  isMultiExamPackage,
} from "src/utils/exams/examReportsHelper";

type EyeCompletionTextType = { exam: ExamType };

const EyeCompletionText = ({ exam }: EyeCompletionTextType) => {
  const { t } = useTranslation();
  const { LEFT, RIGHT } = eyeTypeEnums;

  if (isMultiExamPackage(exam)) {
    return <></>;
  }

  const enumValues = Object.values(examCardEnums);
  const sectionNames = enumValues.map((val) => val.sectionName);
  const sectionName = sectionNames.filter((sectionName) =>
    Boolean(exam?.[sectionName])
  )?.[0];
  const examSections = exam?.[sectionName] ?? [];

  if (isBinocular(examSections) && examSections && examSections?.length > 0) {
    const isTerminated =
      exam?.[sectionName]?.[0]?.completionState === "Terminated";
    const isDone = Boolean(exam?.[sectionName]?.[0]?.completionDate);

    return (
      <Typography
        sx={{
          opacity: isTerminated || isDone ? 1 : 0.5,
          color: isTerminated ? "#FF4A4A" : isDone ? "#22BE39" : "inherit",
          mt: { xs: 0, sm: "14px" },
          fontSize: "14px",
        }}
      >
        {t("Binocular")}
      </Typography>
    );
  }

  if (
    isMonocularMultipleSections(examSections) &&
    examSections &&
    examSections?.length > 1
  ) {
    const leftSection = examSections.find((section) => section?.eye === LEFT);
    const rightSection = examSections.find((section) => section?.eye === RIGHT);

    const leftDone =
      leftSection?.completionState &&
      leftSection?.completionState !== "Undefined";
    const leftTerminated =
      leftSection?.completionState &&
      leftSection?.completionState === "Terminated";

    const rightDone =
      rightSection?.completionState &&
      rightSection?.completionState !== "Undefined";
    const rightTerminated =
      rightSection?.completionState &&
      rightSection?.completionState === "Terminated";

    return (
      <Box display="flex" gap="8px">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {leftTerminated ? (
            <EyeErrorIcon
              style={{ opacity: 1, color: "#FF4A4A" }}
              data-testid="monocular-left-eye-icon"
            />
          ) : leftDone ? (
            <EyeCheckIcon
              style={{ opacity: 1, color: "#22BE39" }}
              data-testid="monocular-left-eye-icon"
            />
          ) : (
            <EyeIcon
              style={{ opacity: 0.5 }}
              data-testid="monocular-left-eye-icon"
            />
          )}
          <Typography
            sx={{
              fontSize: "14px",
              opacity: leftTerminated || leftDone ? 1 : 0.5,
              color: leftTerminated
                ? "#FF4A4A"
                : leftDone
                  ? "#22BE39"
                  : "inherit",
            }}
          >
            {t("Left")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {rightTerminated ? (
            <EyeErrorIcon
              style={{ opacity: 1, color: "#FF4A4A" }}
              data-testid="monocular-right-eye-icon"
            />
          ) : rightDone ? (
            <EyeCheckIcon
              style={{ opacity: 1, color: "#22BE39" }}
              data-testid="monocular-right-eye-icon"
            />
          ) : (
            <EyeIcon
              style={{ opacity: 0.5 }}
              data-testid="monocular-right-eye-icon"
            />
          )}
          <Typography
            sx={{
              fontSize: "14px",
              opacity: rightTerminated || rightDone ? 1 : 0.5,
              color: rightTerminated
                ? "#FF4A4A"
                : rightDone
                  ? "#22BE39"
                  : "inherit",
            }}
          >
            {t("Right")}
          </Typography>
        </Box>
      </Box>
    );
  } else if (examSections && examSections?.length === 1) {
    const isLeftEye = examSections?.[0]?.eye === LEFT;
    const isTerminated = examSections?.[0]?.completionState === "Terminated";
    const isDone = examSections?.[0]?.completionState !== "Undefined";

    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        {isTerminated ? (
          <EyeErrorIcon
            style={{ opacity: 1, color: "#FF4A4A" }}
            data-testid="monocular-one-section-icon"
          />
        ) : isDone ? (
          <EyeCheckIcon
            style={{ opacity: 1, color: "#22BE39" }}
            data-testid="monocular-one-section-icon"
          />
        ) : (
          <EyeIcon
            style={{ opacity: 0.5 }}
            data-testid="monocular-one-section-icon"
          />
        )}
        <Typography
          style={{
            fontSize: "14px",
            opacity: isTerminated || isDone ? 1 : 0.5,
            color: isTerminated ? "#FF4A4A" : isDone ? "#22BE39" : "inherit",
          }}
        >
          {isLeftEye ? t("Left") : t("Right")}
        </Typography>
      </Box>
    );
  }

  return <></>;
};

export default EyeCompletionText;
